import { EpocusRole } from "elephant-web/gql";

/**
 * Canonical ePOCUS UI capability table — the single source of truth for which
 * roles may perform which gated operation across the `/epocus` surface.
 *
 * Each capability is symbolic (one per gated operation) and maps to the ePOCUS
 * roles that possess it. UI code must gate through `useCan(capability)` /
 * `<RoleGate>` rather than checking a role inline.
 *
 * This table mirrors the backend grants 1:1. It derives from:
 *  - PNE-172 — RBAC Foundation (role enum + the base role table)
 *  - PNE-505 — ePOCUS resolver role guards + performer-lock 403s (the
 *              `@authUserRole(roles: [...])` decorators this table must match)
 *  - PNE-507 — PROGRAM_ADMIN org-scoped user management (api-admin Policy)
 *
 * Roles form a broad seniority chain (Performer → Reviewer → QA Admin →
 * Program Admin), but that chain is not a per-capability invariant: authoring
 * is clinician-only and user management is Program-Admin-only. The two
 * deliberate breaks are called out on their capabilities below and locked in by
 * `capabilities.test.ts` so they are not "fixed" by a well-meaning future edit.
 */
export const CAPABILITIES = {
  /**
   * Create a new encounter. Clinician action — performers only (PNE-505).
   * BREAK IN CHAIN: QA Admin does NOT get this — they are program oversight,
   * not clinicians (Open decision §1, resolved read-only). Do not add QaAdmin.
   */
  createEncounter: [EpocusRole.Performer],
  /** Edit one's own in-progress exam/worksheet. Performer-locked (PNE-505). */
  editOwnExam: [EpocusRole.Performer],
  /**
   * Claim an unassigned imported scan off the "To Document" queue. Clinician
   * action, as `createEncounter`. Enforced on push by the IMPORTED → DRAFT
   * transition rather than an `@authUserRole` decorator (PNE-561).
   */
  claimTriageEncounter: [EpocusRole.Performer],
  /** Refer a patient onward. Performing and reviewing clinicians (PNE-505). */
  referPatient: [EpocusRole.Performer, EpocusRole.Reviewer],
  /**
   * See the Referrals section of the nav / surface. Distinct from
   * `referPatient` (the ACTION of referring a patient onward): this gates
   * visibility of the Referrals area itself, and is deliberately clinician-only
   * — program-oversight roles do not see it (PNE-531).
   */
  viewReferrals: [EpocusRole.Performer, EpocusRole.Reviewer],
  /** See the review queue. Reviewers and program oversight (PNE-505). */
  viewReviewQueue: [
    EpocusRole.Reviewer,
    EpocusRole.QaAdmin,
    EpocusRole.ProgramAdmin,
  ],
  /** Submit a review verdict. Reviewers and program oversight (PNE-505). */
  submitReview: [
    EpocusRole.Reviewer,
    EpocusRole.QaAdmin,
    EpocusRole.ProgramAdmin,
  ],
  /** View the program QA dashboard. Oversight roles only (PNE-505). */
  viewProgramDashboard: [EpocusRole.QaAdmin, EpocusRole.ProgramAdmin],
  /**
   * Manage program users. Program Admin only (PNE-507).
   * BREAK IN CHAIN: QA Admin does NOT get this — user management is org-scoped
   * to PROGRAM_ADMIN in api-admin. Do not add EpocusRole.QaAdmin here.
   * (The FE hook `useCanManageProgramUsers` additionally lets internal
   * Elephant staff in — that override is deliberately outside this table.)
   */
  manageProgramUsers: [EpocusRole.ProgramAdmin],
  /** View one's own performance stats. Every ePOCUS role (PNE-505). */
  viewOwnPerformance: [
    EpocusRole.Performer,
    EpocusRole.Reviewer,
    EpocusRole.QaAdmin,
    EpocusRole.ProgramAdmin,
  ],
} satisfies Record<string, readonly EpocusRole[]>;

export type Capability = keyof typeof CAPABILITIES;
